/**
 * Signature widgets — Buy or Sell, Titan Temperature, One Thing We Learned.
 *
 * These are the small "voice of the site" widgets that show up on the
 * homepage and become recognizable daily features. Each fetches its own
 * data from /api/widgets/* which is admin-editable — they degrade to a
 * useful default when the admin hasn't posted today's take yet.
 *
 * Deliberately compact (each ~200-line component) so they stack cleanly on
 * a mobile-first grid without dominating any one section.
 */
import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { TrendingUp, TrendingDown, MinusCircle, Flame, Snowflake, Lightbulb, Clock } from "lucide-react";

const cardCls = "titans-card p-6 relative overflow-hidden h-full";
const eyebrow = "font-cond uppercase tracking-wider text-[10px] mb-2 flex items-center gap-1.5";

// -----------------------------------------------------------------------------
// Buy or Sell
// -----------------------------------------------------------------------------
export function BuyOrSell() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/widgets/buy-or-sell")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => alive && setData(null));
    return () => { alive = false; };
  }, []);
  if (!data) return null;
  const isBuy = data.verdict === "buy";
  const isSell = data.verdict === "sell";
  const chipCls = isBuy
    ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/40"
    : isSell
      ? "bg-titans-red/15 text-titans-red border-titans-red/40"
      : "bg-titans-blue/15 text-titans-blue border-titans-blue/40";
  const Verdict = isBuy ? TrendingUp : isSell ? TrendingDown : MinusCircle;

  return (
    <article
      data-reveal
      className={cardCls}
      data-testid="widget-buy-or-sell"
    >
      <div className={eyebrow + " text-titans-blue"}>
        <Verdict size={12} /> Buy or Sell · The Standard
      </div>
      <div className="flex items-center gap-3 mb-4">
        <span className={`text-2xl md:text-3xl font-display uppercase px-3 py-1 border ${chipCls}`}>
          {data.verdict}
        </span>
        <p className="font-display uppercase text-lg md:text-xl leading-tight tracking-wide">
          {data.subject}
        </p>
      </div>
      <p className="text-white/85 font-body text-sm md:text-base leading-relaxed">{data.take}</p>
      <div className="mt-4 flex items-center justify-between text-[10px] font-cond uppercase tracking-wider text-titans-muted">
        <span>— {data.author}</span>
        {data.updated_at && <span>{formatDate(data.updated_at)}</span>}
      </div>
    </article>
  );
}

// -----------------------------------------------------------------------------
// Titan Temperature — hot/cold meter
// -----------------------------------------------------------------------------
export function TitanTemperature() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/widgets/titan-temperature")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => alive && setData(null));
    return () => { alive = false; };
  }, []);
  if (!data) return null;
  const v = Number(data.value ?? 50);
  const hot = v >= 70;
  const cold = v <= 30;
  const hue = 220 - Math.round((v / 100) * 220); // 220 (blue) → 0 (red)
  const barColor = `hsl(${hue}, 80%, 55%)`;

  return (
    <article
      data-reveal
      className={cardCls}
      data-testid="widget-titan-temperature"
    >
      <div className={eyebrow + " text-titans-red"}>
        {hot ? <Flame size={12} /> : cold ? <Snowflake size={12} /> : <TrendingUp size={12} />} Titan Temperature
      </div>
      <div className="flex items-end gap-3 mb-3">
        <div className="font-display text-6xl md:text-7xl leading-none text-white tabular-nums">{v}°</div>
        <div className="font-display uppercase text-lg text-white">{data.caption}</div>
      </div>
      <div className="h-3 rounded-full overflow-hidden bg-white/10 mb-3" aria-label="Temperature meter">
        <div
          className="h-full reveal-bar"
          style={{ width: `${v}%`, background: `linear-gradient(90deg, hsl(220,80%,55%), ${barColor})` }}
        />
      </div>
      <div className="flex justify-between text-[9px] font-cond uppercase tracking-wider text-titans-muted mb-3">
        <span>Frozen</span><span>Cool</span><span>Warm</span><span>Hot</span><span>Scorching</span>
      </div>
      {data.context && <p className="text-white/80 text-sm font-body">{data.context}</p>}
    </article>
  );
}

// -----------------------------------------------------------------------------
// One Thing We Learned Today
// -----------------------------------------------------------------------------
export function OneThingLearned() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/widgets/one-thing")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => alive && setData(null));
    return () => { alive = false; };
  }, []);
  if (!data) return null;
  return (
    <article
      data-reveal
      className={cardCls}
      data-testid="widget-one-thing"
    >
      <div className={eyebrow + " text-titans-blue"}>
        <Lightbulb size={12} /> {data.tag || "Camp"} · One Thing We Learned
      </div>
      <p className="font-display uppercase leading-tight text-2xl md:text-3xl text-white mb-3">
        {data.lesson}
      </p>
      {data.updated_at && (
        <div className="text-[10px] font-cond uppercase tracking-wider text-titans-muted flex items-center gap-1">
          <Clock size={10} /> {formatDate(data.updated_at)}
        </div>
      )}
    </article>
  );
}
